import { environment } from '../config/environment';
import { useApiClient } from './api';
import {
    AccountDistributionData,
    AccountDistributionDataForCoin,
    ChartValue,
    CurrencyBalance,
    DashboardData,
    LivePrice,
    TokenValue,
} from './dashboard.types';
import { RawChartValue } from './raw.types';
import { transformChartValue } from './transformers';

const useDashboardService = () => {
    const apiClient = useApiClient();

    const getBalanceData = async (fromTime: number, isBtc: boolean): Promise<DashboardData> => {
        try {
            const response = await apiClient.get<DashboardData>(`${environment.ctLiteApiUrl}/dashboard/balance`, {
                params: { fromTime, isBtc: +isBtc },
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching dashboard balance data:', error);
            throw error;
        }
    };

    const getFiatInvested = async (): Promise<any> => {
        try {
            const response = await apiClient.get<any>(`${environment.ctLiteApiUrl}/dashboard/fiat-invested`);
            return response.data;
        } catch (error) {
            console.error('Error fetching fiat invested:', error);
            throw error;
        }
    };

    const getTotalProceeds = async (): Promise<any> => {
        try {
            const response = await apiClient.get<any>(`${environment.ctLiteApiUrl}/dashboard/total-proceeds`);
            return response.data;
        } catch (error) {
            console.error('Error fetching total proceeds:', error);
            throw error;
        }
    };

    const getUnrealizedGainsData = async (): Promise<any> => {
        try {
            const response = await apiClient.get<any>(`${environment.ctLiteApiUrl}/dashboard/unrealized-gains`);
            return response.data;
        } catch (error) {
            console.error('Error fetching unrealized gains data:', error);
            throw error;
        }
    };

    const getTopTokenAllocation = async (): Promise<TokenValue[]> => {
        try {
            const response = await apiClient.get<TokenValue[]>(
                `${environment.ctLiteApiUrl}/dashboard/top-token-allocation`
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching top token allocation data data:', error);
            throw error;
        }
    };

    const getPortfolioValueHistory = async (fromTime: number, isBtc: boolean): Promise<ChartValue[]> => {
        try {
            const response = await apiClient.get<RawChartValue[]>(
                `${environment.ctLiteApiUrl}/dashboard/profit-coins`,
                {
                    params: { fromTime, isBtc: +isBtc },
                }
            );
            return transformChartValue(response.data);
        } catch (error) {
            console.error('Error fetching portfolio value history:', error);
            throw error;
        }
    };

    const getBalancesPerCurrency = async (): Promise<CurrencyBalance[]> => {
        try {
            const response = await apiClient.get(`${environment.ctLiteApiUrl}/dashboard/balance`);
            return response.data;
        } catch (error) {
            console.error('Error fetching balances per currency:', error);
            throw error;
        }
    };

    const downloadBalancesCsv = async (isBtc: boolean): Promise<void> => {
        try {
            const response = await apiClient.get(`${environment.ctLiteApiUrl}/dashboard/balance-csv`, {
                params: { btc: Number(isBtc) },
                responseType: 'blob',
            });

            const downloadUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `Balance ${new Date().toISOString()}.csv`;
            link.click();

            // Clean up the URL object after download starts
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading balances CSV:', error);
            throw error;
        }
    };

    const downloadBalancesExcel = async (isBtc: boolean): Promise<void> => {
        try {
            const response = await apiClient.get(`${environment.ctLiteApiUrl}/dashboard/balance-excel`, {
                params: { btc: Number(isBtc) },
                responseType: 'blob',
            });

            const downloadUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `Balance ${new Date().toISOString()}.xlsx`;
            link.click();

            // Clean up the URL object after download starts
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading balances Excel:', error);
            throw error;
        }
    };

    const downloadBalancesPdf = async (isBtc: boolean): Promise<void> => {
        try {
            const response = await apiClient.get(`${environment.ctLiteApiUrl}/dashboard/balance-pdf`, {
                params: { btc: Number(isBtc) },
                responseType: 'blob',
            });

            const downloadUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `Balance ${new Date().toISOString()}.pdf`;
            link.click();

            // Clean up the URL object after download starts
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading balances PDF:', error);
            throw error;
        }
    };

    const getBalance = async () => {
        try {
            const response = await apiClient.get<AccountDistributionData[]>(
                `${environment.ctLiteApiUrl}/dashboard/balance`
            );
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getCoinBalance = async (coin: string) => {
        try {
            const params = new URLSearchParams();
            params.append('coin', coin);
            const response = await apiClient.get<AccountDistributionData[]>(
                `${environment.ctLiteApiUrl}/dashboard/balance/coin`,
                { params }
            );
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getCoinBalancePreload = async (coins: string[]) => {
        try {
            const params = new URLSearchParams();
            coins.forEach((coin) => params.append('coins[]', coin));
            const response = await apiClient.get<AccountDistributionDataForCoin[]>(
                `${environment.ctLiteApiUrl}/dashboard/balance/coin-preload`,
                {
                    params,
                }
            );
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const refreshBalance = async () => {
        try {
            const response = await apiClient.post(`${environment.ctLiteApiUrl}/dashboard/balance/refresh`);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    return {
        getBalanceData,
        getPortfolioValueHistory,
        getBalancesPerCurrency,
        downloadBalancesCsv,
        downloadBalancesExcel,
        downloadBalancesPdf,
        getBalance,
        getCoinBalance,
        getCoinBalancePreload,
        refreshBalance,
        getUnrealizedGainsData,
        getTopTokenAllocation,
        getFiatInvested,
        getTotalProceeds,
    };
};

export default useDashboardService;
